import { Component, Input, OnInit } from '@angular/core';
import { MappingService } from '../../../services/mapping.service';
import { DynamicPin } from '../../../models/dynamicPin';
import { ContextualDataItem } from '../../models/inspection-report.model';

interface ContextualGroup {
  type: string;
  period: string;
  items: ContextualDataItem[];
}

@Component({
  selector: 'app-contextual-data-panel',
  templateUrl: './contextual-data-panel.component.html',
  standalone: false
})
export class ContextualDataPanelComponent implements OnInit {

  @Input() tenantId: number = 0;
  @Input() fieldId: number = 0;
  @Input() lat: number = 0;
  @Input() lng: number = 0;

  /**
   * Analysis values captured against a saved report. When this is set — including
   * when it is an empty array — the panel renders it and makes no live request:
   * a saved report must show what was captured, not what the analysis says today.
   * Left null only for an inspection that has not been saved yet.
   */
  @Input() capturedData: ContextualDataItem[] | null = null;

  groups: ContextualGroup[] = [];
  loading = false;

  constructor(private mappingService: MappingService) {}

  ngOnInit(): void {
    if (this.capturedData) {
      this.groups = this.groupByType(this.capturedData);
      return;
    }
    this.loadLive();
  }

  /** Only reached for an unsaved inspection — nothing has been captured for it yet. */
  private loadLive(): void {
    if (!this.tenantId || !this.fieldId || !this.lat || !this.lng) return;
    this.loading = true;
    this.mappingService.getDynamicPinData(this.tenantId, this.fieldId, this.lat, this.lng)
      .subscribe({
        next: (data) => {
          this.groups = this.groupByType((data ?? []).map(p => this.fromDynamicPin(p)));
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  /** Normalise the live findForPin shape onto the captured shape so one template serves both. */
  private fromDynamicPin(pin: DynamicPin): ContextualDataItem {
    return {
      type: pin.type,
      label: pin.chartName,
      name: pin.name,
      value: pin.value,
      period: pin.maxPeriod,
      chartId: pin.chartId,
      isNumeric: pin.isNumericProperty,
      minValue: pin.chartMinValue ?? null,
      maxValue: pin.chartMaxValue ?? null
    };
  }

  /** Grouped once on load rather than in the template — the view must not recompute per cycle. */
  private groupByType(items: ContextualDataItem[]): ContextualGroup[] {
    const map = new Map<string, ContextualGroup>();
    items.forEach(item => {
      const type = item.type || 'Analysis';
      if (!map.has(type)) {
        map.set(type, { type, period: item.period, items: [] });
      }
      map.get(type)!.items.push(item);
    });
    return Array.from(map.values());
  }

  trackByType(_: number, group: ContextualGroup): string {
    return group.type;
  }

  trackByChartId(_: number, item: ContextualDataItem): number | string {
    return item.chartId ?? item.label;
  }
}
